import { Fragment, useEffect, useMemo, useState } from 'react';

import { VSpacing, Text } from '@hh.ru/magritte-ui';

import paths from 'src/app/routePaths';
import { CONTENT_ID } from 'src/components/A11y/A11yConstants';
import ContainerForMicroFrontend from 'src/components/CodeInjector/ContainerForMicroFrontend';
import SearchExplanation from 'src/components/Debug/SearchExplanation';
import SearchRating from 'src/components/SearchRating';
import NeighboursAreaBanner from 'src/components/VacancySearchContent/components/NeighboringAreaBanner';
import useShowNeighboursAreaBanner from 'src/components/VacancySearchContent/components/NeighboringAreaBanner/hooks/useShowNeighboursAreaBanner';
import SavedSearchSubscription from 'src/components/VacancySearchContent/components/SavedSearchSubscription';
import VacancyHint from 'src/components/VacancySearchContent/components/VacancyHint';
import VacancySearchError from 'src/components/VacancySearchContent/components/VacancySearchError';
import VacancyWizard from 'src/components/VacancySearchContent/components/VacancyWizard/Magritte';
import VacancySearchItem from 'src/components/VacancySearchItem';
import Source from 'src/components/VacancySearchItem/types/Source';
import translation from 'src/components/translation';
import { useSelector } from 'src/hooks/useSelector';

import BannersUnderResult from 'src/components/VacancySearchContent/components/SearchResult/BannersUnderResult';
import SearchResultBanners from 'src/components/VacancySearchContent/components/SearchResult/SearchResultBanners';

const VACANCY_SEARCH_RATING = 'vacancy_search_rating';
const DEFAULT_RATING_POSITION = 24;
const CAREER_PLATFORM_POSITION = 4;

const SearchResult = ({ trls }) => {
    const hasApplicantBanner = useSelector((state) => !!state.microFrontends.careerSearchBanner);
    const vacancySearchResult = useSelector((state) => state.vacancySearchResult);
    const wizardPosition = useSelector((state) => state.vacancyWizard.position);
    const features = useSelector((state) => state.features);
    const isSearchPage = useSelector(({ router }) => router.location.pathname.startsWith(paths.vacancySearch));
    const [vacanciesNumber, setVacanciesNumber] = useState(
        isSearchPage ? features[SearchResult.features.numberOfVacanciesRenderedOnServer] : 0
    );

    const isShowNeighboursAreaBanner = useShowNeighboursAreaBanner();

    const vacancies = vacancySearchResult.vacancies;
    useEffect(() => {
        if (vacanciesNumber && vacancies.length > vacanciesNumber) {
            // 0 - render all vacancies
            setVacanciesNumber(0);
        }
    }, [vacancies.length, vacanciesNumber]);

    const vacanciesToRender = useMemo(
        () => (!vacanciesNumber ? vacancies : vacancies.slice(0, vacanciesNumber)),
        [vacancies, vacanciesNumber]
    );

    if (!vacancySearchResult) {
        return <VacancySearchError />;
    }

    const { savedSearches, vacancyHint, criteria, totalResults, queryRanks } = vacancySearchResult;

    const ratingPosition = Math.min(vacanciesToRender.length - 1, DEFAULT_RATING_POSITION);

    return (
        <>
            {!isShowNeighboursAreaBanner && totalResults === 0 && queryRanks.length === 0 && (
                <>
                    <VSpacing default={12} gteM={24} />
                    <Text>{trls[SearchResult.trls.notFound]}</Text>
                    <VSpacing default={24} gteM={0} />
                </>
            )}
            {vacancyHint && <VacancyHint hintType={vacancyHint} />}
            <div data-qa="vacancy-serp__results" id={CONTENT_ID}>
                <SearchExplanation />
                <VSpacing default={12} gteM={24} />
                {!isShowNeighboursAreaBanner && savedSearches.position === 0 && <SavedSearchSubscription />}
                {vacanciesToRender.map((vacancy, index) => (
                    <Fragment key={vacancy.vacancyId}>
                        <SearchResultBanners vacancyPosition={index} />
                        <VacancySearchItem
                            key={vacancy.vacancyId}
                            vacancy={vacancy}
                            vacancySource={Source.VacancySearch}
                            criteriaText={criteria.text}
                        />
                        {index + 1 === wizardPosition && <VacancyWizard />}
                        {!isShowNeighboursAreaBanner && index + 1 === savedSearches.position && (
                            <SavedSearchSubscription />
                        )}
                        {ratingPosition === index && (
                            <SearchRating
                                portfolio="PORTFOLIO-14535"
                                eventName="select_search_rating"
                                infoTipName={VACANCY_SEARCH_RATING}
                            />
                        )}
                        {hasApplicantBanner && index + 1 === CAREER_PLATFORM_POSITION && (
                            <>
                                {hasApplicantBanner && <ContainerForMicroFrontend place={'careerSearchBanner'} />}
                                <VSpacing default={16} />
                            </>
                        )}
                    </Fragment>
                ))}
                {isShowNeighboursAreaBanner && <NeighboursAreaBanner />}
            </div>
            <BannersUnderResult countVacancies={vacanciesToRender.length} />
        </>
    );
};

SearchResult.trls = {
    notFound: 'search.vacancy.changeParamsOrKeywords',
};

SearchResult.features = {
    numberOfVacanciesRenderedOnServer: 'max_vacancies_rendered_on_server',
};

export default translation(SearchResult);
